import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { PenTool } from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";

const Auth = () => {
  const { login, register, loading, user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [remember, setRemember] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (mode === "register") {
        await register(email, password, displayName || email.split("@")[0], remember);
        toast.success("Welcome. Your study is open.");
      } else {
        await login(email, password, remember);
        toast.success("Welcome back.");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 grain-overlay flex items-center justify-center px-6" data-testid="auth-page">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center gap-3 justify-center mb-12" data-testid="auth-logo-link">
          <PenTool className="w-4 h-4 text-rose-800" strokeWidth={1.5} />
          <span className="font-serif-heading text-2xl tracking-wide text-stone-100">Inkwell</span>
        </Link>

        <div className="border border-stone-800 bg-stone-950/60 backdrop-blur p-10 md:p-12">
          <div className="overline text-xs font-sans-ui uppercase tracking-[0.3em] text-stone-500 mb-3">
            {mode === "register" ? "Chapter I" : "Returning"}
          </div>
          <h1 className="font-serif-heading text-4xl font-light text-stone-100 mb-10 leading-tight">
            {mode === "register" ? "Open your study." : "Welcome back."}
          </h1>

          <form onSubmit={submit} className="space-y-6">
            {mode === "register" && (
              <div>
                <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Name (as you sign your chapters)</Label>
                <Input
                  data-testid="auth-name-input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="mt-3 bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100 font-serif-reading focus:ring-rose-900 focus:border-rose-900"
                  placeholder="Ines, or a pseudonym"
                />
              </div>
            )}
            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Email</Label>
              <Input
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-3 bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100 font-serif-reading focus:ring-rose-900"
                placeholder="you@quiet.address"
              />
            </div>
            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Password</Label>
              <Input
                data-testid="auth-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-3 bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100 font-serif-reading focus:ring-rose-900"
                placeholder="at least six characters"
              />
            </div>

            <div className="flex items-center gap-3 pt-1 select-none">
              <input
                id="remember-me"
                data-testid="auth-remember-checkbox"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 appearance-none border border-stone-700 bg-stone-900 checked:bg-rose-900 checked:border-rose-800 focus:outline-none focus:ring-1 focus:ring-rose-900 transition relative cursor-pointer
                  checked:after:content-['✓'] checked:after:text-rose-50 checked:after:text-[11px] checked:after:absolute checked:after:inset-0 checked:after:flex checked:after:items-center checked:after:justify-center checked:after:leading-none"
              />
              <label htmlFor="remember-me" className="font-sans-ui text-[11px] uppercase tracking-[0.22em] text-stone-500 hover:text-stone-300 cursor-pointer transition">
                Keep me signed in on this device
              </label>
            </div>

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-12 font-sans-ui text-xs uppercase tracking-[0.25em] mt-4"
            >
              {loading ? "A moment…" : mode === "register" ? "Enter" : "Sign in"}
            </Button>
          </form>

          <div className="mt-4 -mb-2 text-center">
            <p className="font-serif-reading italic text-stone-600 text-xs leading-relaxed">
              {remember
                ? "You\u2019ll stay signed in until you sign out."
                : "You\u2019ll be signed out when you close this tab."}
            </p>
          </div>

          <div className="mt-8 text-center">
            <button
              data-testid="auth-toggle-mode-btn"
              type="button"
              onClick={() => setMode(mode === "register" ? "login" : "register")}
              className="font-sans-ui text-xs uppercase tracking-[0.25em] text-stone-500 hover:text-stone-200 transition"
            >
              {mode === "register" ? "Already have a study?  Sign in" : "New here?  Begin a study"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
